import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Sparkles, Image as ImageIcon, Camera, ChevronUp, ChevronDown,
  MessageSquareText, Send, ShieldCheck, Download, Bell, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  not_requested: "Not requested",
  review_requested: "Review requested",
  collage_delivered: "Collage delivered",
  pending_verification: "Pending verification",
  verified: "Verified",
  discount_approved: "Discount approved",
  discount_applied: "Discount applied",
};

const STATUS_COLORS = {
  not_requested: "bg-zinc-100 text-zinc-700",
  review_requested: "bg-blue-100 text-blue-800",
  collage_delivered: "bg-blue-100 text-blue-800",
  pending_verification: "bg-yellow-100 text-yellow-800",
  verified: "bg-emerald-100 text-emerald-800",
  discount_approved: "bg-emerald-100 text-emerald-800",
  discount_applied: "bg-emerald-100 text-emerald-800",
};

/**
 * Post-Job Marketing Automation panel.
 * Lives inside JobProfile (only visible once job is completed).
 *
 * Flow: pick 2..6 photos → label before/after → reorder → Generate collage →
 * Preview → Send review-request SMS → manual Verify Review → discount approved.
 */
export default function MarketingAutomationPanel({ job, business, onJobRefresh }) {
  const [uploads, setUploads] = useState([]);
  const [selection, setSelection] = useState([]); // [{upload_id, order, label, selected}]
  const [collage, setCollage] = useState(null);   // {id, public_url, ...}
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNote, setVerifyNote] = useState("");
  const [photoConfirmed, setPhotoConfirmed] = useState(true);

  const minP = Number(business?.postjob_min_photos ?? 2);
  const maxP = Number(business?.postjob_max_photos ?? 6);

  const load = async () => {
    try {
      const [u, r] = await Promise.all([
        api.get(`/uploads?resource_type=job&resource_id=${job.id}`),
        api.get(`/jobs/${job.id}/marketing/review`),
      ]);
      const imgs = (u.data || []).filter(
        (f) => (f.content_type || "").startsWith("image/") && (f.kind !== "marketing_collage")
      );
      setUploads(imgs);
      setReview(r.data);

      // Merge existing selection with fresh uploads (so newly uploaded photos
      // appear as unselected in the picker).
      const stored = (job.marketing_photo_selection || []).reduce((acc, s) => {
        acc[s.upload_id] = s;
        return acc;
      }, {});
      const merged = imgs.map((u, idx) => stored[u.id] || { upload_id: u.id, order: idx, label: null, selected: false });
      // Preserve stored order for stored entries; append new ones after.
      merged.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setSelection(merged);

      if (job.marketing_collage_upload_id) {
        // Look up collage from uploads list may not include it; refetch just meta
        setCollage({
          id: job.marketing_collage_upload_id,
          public_url: null,
          signed_url: `/api/uploads/${job.marketing_collage_upload_id}/download`,
        });
      } else {
        setCollage(null);
      }
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { if (job?.id) load(); }, [job?.id, job?.marketing_collage_upload_id]); // eslint-disable-line

  const selectedCount = selection.filter((s) => s.selected).length;
  const canGenerate = selectedCount >= minP && selectedCount <= maxP;

  const toggleSelect = (upload_id) =>
    setSelection((s) => s.map((r) => (r.upload_id === upload_id ? { ...r, selected: !r.selected } : r)));

  const setLabel = (upload_id, label) =>
    setSelection((s) => s.map((r) => (r.upload_id === upload_id ? { ...r, label } : r)));

  const move = (idx, dir) => {
    setSelection((s) => {
      const next = [...s];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return next;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((r, i) => ({ ...r, order: i }));
    });
  };

  const saveSelection = async () => {
    setBusy(true);
    try {
      const payload = selection.map((r, i) => ({ ...r, order: i, label: r.label || null }));
      await api.post(`/jobs/${job.id}/marketing/photos/select`, { photos: payload });
      toast.success(`Saved ${selectedCount} photo${selectedCount === 1 ? "" : "s"}`);
      onJobRefresh && onJobRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save selection");
    } finally { setBusy(false); }
  };

  const generateCollage = async () => {
    setBusy(true);
    try {
      // Ensure current selection is saved first (idempotent).
      await api.post(`/jobs/${job.id}/marketing/photos/select`, {
        photos: selection.map((r, i) => ({ ...r, order: i, label: r.label || null })),
      });
      const { data } = await api.post(`/jobs/${job.id}/marketing/collage/generate`);
      setCollage(data);
      toast.success("Photo collage generated");
      onJobRefresh && onJobRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate collage");
    } finally { setBusy(false); }
  };

  const sendReviewRequest = async () => {
    if (!business?.google_review_url) {
      toast.error("Set your Google Review URL in Business Settings first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/marketing/review-request/send`, {});
      setReview(data.review);
      toast.success(data.already_sent ? "Review request already sent" : "Review request SMS sent");
      onJobRefresh && onJobRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send review request");
    } finally { setBusy(false); }
  };

  const openVerify = () => { setPhotoConfirmed(true); setVerifyNote(""); setVerifyOpen(true); };
  const doVerify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reviews/${review.id}/verify`, {
        photo_included: photoConfirmed,
        reviewer_note: verifyNote,
      });
      setReview(data);
      toast.success(
        data.status === "discount_approved" ? "Review verified — discount approved"
        : data.status === "pending_verification" ? "Marked pending — photo not confirmed"
        : "Review updated"
      );
      setVerifyOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to verify");
    } finally { setBusy(false); }
  };

  const sendReminder = async () => {
    setBusy(true);
    try {
      await api.post(`/reviews/${review.id}/reminder/send`);
      toast.success("Reminder SMS sent");
      const { data } = await api.get(`/jobs/${job.id}/marketing/review`);
      setReview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminder");
    } finally { setBusy(false); }
  };

  const enabled = business?.postjob_marketing_enabled !== false;
  const completed = job?.status === "completed";
  const status = review?.status || "not_requested";
  const collageDownloadUrl = collage?.id
    ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/uploads/${collage.id}/download`
    : null;

  if (!enabled) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="marketing-panel-disabled">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-zinc-400"/>
          <h2 className="font-heading text-xl font-bold text-zinc-500">Post-job marketing (disabled)</h2>
        </div>
        <p className="text-sm text-zinc-500">
          Post-job marketing automation is turned off in Business Settings.
        </p>
      </div>
    );
  }

  if (!completed) {
    return (
      <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp" data-testid="marketing-panel-locked">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-5 h-5 text-[#FF5A00]"/>
          <h2 className="font-heading text-xl font-bold">Post-job marketing</h2>
        </div>
        <p className="text-sm text-zinc-500">
          Mark this job as <span className="font-medium text-zinc-700">Completed</span> to start the
          Google-review workflow (photo collage → optional NEUTRAL review request → separate photo/social-share promotion → verified discount).
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 card-crisp space-y-5" data-testid="marketing-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-[#FF5A00]"/>
        <h2 className="font-heading text-xl font-bold">Post-job marketing</h2>
        <span
          data-testid="marketing-status-badge"
          className={`ml-auto text-[10px] uppercase tracking-widest font-mono-accent px-2 py-1 rounded ${STATUS_COLORS[status]}`}
        >
          {STATUS_LABEL[status]}
        </span>
      </div>

      {/* Compliance banner — Iter45 */}
      <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="marketing-compliance-banner">
        <strong>Google TOS compliance:</strong> the {business?.photo_promotion_discount_pct ?? business?.review_discount_pct ?? 10}% discount below is earned by
        photo-use permission or sharing our finished-project post — <em>never</em> by leaving a Google
        review. Google prohibits offering incentives for reviews, and violating this can get reviews
        removed or the business profile penalized.
      </div>

      {/* ---------- Photo picker ---------- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">1. Select {minP}–{maxP} job photos</div>
          <div className="text-xs text-zinc-500 font-mono-accent" data-testid="marketing-photo-count">
            {selectedCount}/{maxP} selected
          </div>
        </div>
        {uploads.length === 0 ? (
          <div className="text-sm text-zinc-500 py-4 flex items-center gap-2">
            <Camera className="w-4 h-4"/> Upload job photos via the uploader on the right first.
          </div>
        ) : (
          <div className="space-y-2 max-h-[380px] overflow-y-auto">
            {selection.map((row, idx) => {
              const u = uploads.find((x) => x.id === row.upload_id);
              if (!u) return null;
              const src = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${u.signed_url || `/api/uploads/${u.id}/download`}`;
              return (
                <div
                  key={u.id}
                  className={`flex items-center gap-3 border rounded-lg p-2 ${row.selected ? "border-[#FF5A00] bg-orange-50" : "border-zinc-200"}`}
                  data-testid={`marketing-photo-row-${u.id}`}
                >
                  <input
                    type="checkbox"
                    checked={!!row.selected}
                    onChange={() => toggleSelect(u.id)}
                    className="w-4 h-4 accent-[#FF5A00]"
                    data-testid={`marketing-photo-select-${u.id}`}
                  />
                  <img src={src} alt="" className="w-14 h-14 object-cover rounded-md border border-zinc-200" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-zinc-500 truncate">{u.original_filename}</div>
                    <div className="mt-1 flex items-center gap-1">
                      {["before", "after", "progress", null].map((lbl) => {
                        const isSel = (row.label || null) === lbl;
                        return (
                          <button
                            key={String(lbl)}
                            type="button"
                            onClick={() => setLabel(u.id, lbl)}
                            className={`text-[10px] uppercase font-mono-accent tracking-widest px-2 py-0.5 rounded ${isSel ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                            data-testid={`marketing-label-${lbl || "none"}-${u.id}`}
                          >
                            {lbl || "none"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <button
                      className="text-zinc-400 hover:text-zinc-900"
                      onClick={() => move(idx, -1)}
                      data-testid={`marketing-move-up-${u.id}`}
                    >
                      <ChevronUp className="w-4 h-4"/>
                    </button>
                    <button
                      className="text-zinc-400 hover:text-zinc-900"
                      onClick={() => move(idx, 1)}
                      data-testid={`marketing-move-down-${u.id}`}
                    >
                      <ChevronDown className="w-4 h-4"/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="outline"
            onClick={saveSelection}
            disabled={busy || selectedCount === 0}
            data-testid="marketing-save-selection-btn"
          >
            Save selection
          </Button>
          <Button
            className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift"
            disabled={busy || !canGenerate}
            onClick={generateCollage}
            data-testid="marketing-generate-collage-btn"
          >
            <ImageIcon className="w-4 h-4 mr-1"/>
            {collage ? "Regenerate collage" : "Generate collage"}
          </Button>
          {!canGenerate && (
            <div className="text-xs text-zinc-500">
              Select {minP}–{maxP} photos to enable collage.
            </div>
          )}
        </div>
      </div>

      {/* ---------- Collage preview ---------- */}
      {collage?.id && (
        <div className="border border-zinc-200 rounded-lg p-3" data-testid="marketing-collage-preview">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-medium">2. Preview collage</div>
            {collageDownloadUrl && (
              <a
                href={collageDownloadUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#FF5A00] hover:underline flex items-center gap-1"
                data-testid="marketing-collage-download"
              >
                <Download className="w-3 h-3"/> Download
              </a>
            )}
          </div>
          <img
            src={collageDownloadUrl}
            alt="Collage preview"
            className="w-full max-h-[420px] object-contain rounded-md bg-zinc-50"
          />
        </div>
      )}

      {/* ---------- Review request ---------- */}
      <div className="rounded-lg border border-blue-100 bg-blue-50/40 p-3" data-testid="marketing-google-review-block">
        <div className="text-sm font-medium mb-1">3a. Send a NEUTRAL Google review request</div>
        <p className="text-xs text-zinc-600 mb-2">
          Google prohibits offering any discount, gift, or reward in exchange for a review. This text is
          strictly a neutral thank-you with a review link — <span className="font-medium">no discount is
          attached, and none should ever be tied to leaving a review</span>.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            className="bg-zinc-900 hover:bg-black text-white"
            disabled={busy || !business?.google_review_url}
            onClick={sendReviewRequest}
            data-testid="marketing-send-request-btn"
          >
            <Send className="w-4 h-4 mr-1"/> Send neutral review request
          </Button>
          {review?.review_requested_at && (
            <div className="text-xs text-zinc-500">
              Legacy discount-linked request sent {new Date(review.review_requested_at).toLocaleString()} —
              this predates the compliance rebuild.
            </div>
          )}
        </div>
        {!business?.google_review_url && (
          <div className="text-xs text-red-600 mt-2 flex items-center gap-1">
            <MessageSquareText className="w-3 h-3"/> Set Google Review URL in Business Settings first.
          </div>
        )}
      </div>

      <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-testid="marketing-photo-promotion-block">
        <div className="text-sm font-medium mb-1">3b. Send the photo / social-share promotion</div>
        <p className="text-xs text-zinc-600 mb-2">
          Customers can earn the {business?.photo_promotion_discount_pct ?? 10}% discount by granting
          photo-use permission OR sharing the finished-project post — completely independent from any
          Google review.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            disabled={busy || !collage?.id}
            onClick={sendReviewRequest}
            data-testid="marketing-send-promotion-btn"
            title="Currently reuses the legacy send-review-request endpoint — full split UI lands in the next release."
          >
            <Send className="w-4 h-4 mr-1"/> Send photo/social promotion
          </Button>
          <div className="text-xs text-zinc-500">
            Discount: {business?.photo_promotion_discount_pct ?? business?.review_discount_pct ?? 10}%
          </div>
        </div>
      </div>

      {/* ---------- Verify + reminder ---------- */}
      {review && ["collage_delivered", "review_requested", "pending_verification"].includes(status) && (
        <div className="border-t border-zinc-100 pt-4">
          <div className="text-sm font-medium mb-2">4. Verify photo/social promotion</div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={openVerify}
              disabled={busy}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="marketing-verify-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-1"/> Verify review
            </Button>
            <Button
              variant="outline"
              onClick={sendReminder}
              disabled={busy || (review.reminder_sent_count >= Number(business?.review_reminder_max_count ?? 1))}
              data-testid="marketing-reminder-btn"
            >
              <Bell className="w-4 h-4 mr-1"/>
              Send reminder ({review.reminder_sent_count || 0}/{business?.review_reminder_max_count ?? 1})
            </Button>
          </div>
        </div>
      )}

      {review && ["verified", "discount_approved", "discount_applied"].includes(status) && (
        <div className="border-t border-zinc-100 pt-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="marketing-verified-state">
          <ShieldCheck className="w-4 h-4"/>
          Discount {review.discount_pct}% {status === "discount_applied" ? "applied" : "approved"}
          {review.verified_at ? ` on ${new Date(review.verified_at).toLocaleDateString()}` : ""}
        </div>
      )}

      {/* Verify dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="sm:max-w-md" data-testid="marketing-verify-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Verify photo/social promotion</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-600">
              Confirm the customer completed at least one qualifying action —
              granted photo-use permission for the collage OR shared the finished-project post on their social.
              <strong className="text-red-600"> This must NEVER be tied to leaving a Google review.</strong>
            </p>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={photoConfirmed}
                onChange={(e) => setPhotoConfirmed(e.target.checked)}
                className="w-4 h-4 accent-[#FF5A00]"
                data-testid="marketing-verify-photo-checkbox"
              />
              <span>Customer granted photo-use permission OR shared our post</span>
            </label>
            <div>
              <Label>Note (optional)</Label>
              <Textarea
                rows={2}
                value={verifyNote}
                onChange={(e) => setVerifyNote(e.target.value)}
                data-testid="marketing-verify-note"
                placeholder="e.g. Customer replied YES on 7/23, or posted to Facebook…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyOpen(false)}>Cancel</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={busy}
              onClick={doVerify}
              data-testid="marketing-verify-confirm-btn"
            >
              {busy ? "Verifying…" : "Confirm & approve discount"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
